// Ticker engine: pure functions that detect events from scoring data
// and generate headline strings for the ESPN-style stock ticker.

import { getDormieSegments, computeSegmentMatchStatus } from './scoringEngine'

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)]

// ============================================
// Headline templates
// ============================================

const HEADLINES = {
  ace: (name, hole) => pick([
    `ACE ALERT! ${name} on #${hole}!`,
    `HOLE IN ONE! ${name} aces #${hole}!`,
    `${name} aces #${hole}! Drinks on them!`,
    `Stop everything — ${name} aced #${hole}!`,
  ]),
  match_tied: (gameName, hole) => {
    const prefix = gameName ? `${gameName}: ` : ''
    return pick([
      `${prefix}All square through #${hole}!`,
      `${prefix}Tied up through #${hole}!`,
      `${prefix}Match is tied through #${hole}!`,
    ])
  },
  lead_flip: (gameName, teamName, hole) => {
    const prefix = gameName ? `${gameName}: ` : ''
    return pick([
      `${prefix}Lead change! ${teamName} up through #${hole}!`,
      `${prefix}${teamName} flips the match through #${hole}!`,
      `${prefix}${teamName} takes the lead through #${hole}!`,
    ])
  },
  segment_clinched: (gameName, teamName, segment, text) => {
    const prefix = gameName ? `${gameName}: ` : ''
    return pick([
      `${prefix}${teamName} clinches the ${segment} ${text}!`,
      `${prefix}${teamName} wins the ${segment} ${text}!`,
      `${prefix}${teamName} locks up the ${segment} ${text}!`,
    ])
  },
  dormie: (gameName, teamName, segment, remaining) => {
    const prefix = gameName ? `${gameName}: ` : ''
    return pick([
      `${prefix}${teamName} is dormie on the ${segment} — ${remaining} to play!`,
      `${prefix}Dormie! ${teamName} must win out on the ${segment}!`,
      `${prefix}${teamName} on the ropes — dormie on the ${segment}, ${remaining} hole${remaining === 1 ? '' : 's'} left!`,
    ])
  },
}

const SEGMENT_LABELS = { front: 'front', back: 'back', overall: 'match' }

// ============================================
// Score-based event detection
// ============================================

/**
 * Detect events from a single score entry.
 * Only emits ace events (hole-in-one).
 */
export function detectScoreEvents({ gameId, matchId, playerId, playerName, holeNumber, grossScore, par }) {
  if (grossScore == null || par == null) return []

  if (grossScore === 1) {
    return [{
      game_id: gameId,
      match_id: matchId,
      event_type: 'ace',
      player_id: playerId,
      hole_number: holeNumber,
      headline: HEADLINES.ace(playerName, holeNumber),
    }]
  }

  return []
}

// ============================================
// Clinch detection
// ============================================

/**
 * Detect when a nassau segment is clinched by comparing prev/curr results.
 * Uses segment-suffixed event_type (e.g. segment_clinched_front) to avoid
 * unique index conflicts when multiple segments clinch on the same hole.
 */
export function detectClinchEvents({ prevResults, currResults, gameId, matchId, holeNumber, teams, gameName }) {
  if (!prevResults || !currResults || !teams || teams.length < 2) return []
  if (currResults.format !== 'nassau' || !currResults.nassau) return []
  if (!prevResults.nassau) return []

  const events = []

  for (const seg of ['front', 'back', 'overall']) {
    const prevData = prevResults.nassau[seg]
    const currData = currResults.nassau[seg]
    if (!currData) continue

    // Check if segment just became finished (complete or clinched)
    const ranges = { front: [1, 9], back: [10, 18], overall: [1, 18] }
    const [start, end] = ranges[seg]
    const status = computeSegmentMatchStatus(currResults.holeResults, start, end)
    const isFinished = currData.complete || status?.clinchHole
    const wasFinished = prevData?.complete ||
      (prevResults.holeResults && computeSegmentMatchStatus(prevResults.holeResults, start, end)?.clinchHole)

    if (isFinished && !wasFinished && currData.winner != null) {
      const teamName = teams[currData.winner]?.name || `Team ${currData.winner + 1}`
      const segLabel = SEGMENT_LABELS[seg]
      const clinchText = status?.text || ''

      events.push({
        game_id: gameId,
        match_id: matchId,
        event_type: `segment_clinched_${seg}`,
        player_id: null,
        hole_number: holeNumber,
        headline: HEADLINES.segment_clinched(gameName, teamName, segLabel, clinchText),
      })
    }
  }

  return events
}

// ============================================
// Dormie detection
// ============================================

/**
 * Detect when a team becomes dormie by comparing prev/curr results.
 * Uses segment-suffixed event_type (e.g. dormie_front).
 */
export function detectDormieEvents({ prevResults, currResults, gameId, matchId, holeNumber, teams, gameName }) {
  if (!currResults || !teams || teams.length < 2) return []

  const prevDormies = getDormieSegments(prevResults)
  const currDormies = getDormieSegments(currResults)

  const prevSet = new Set(prevDormies.map((d) => d.segment))
  const events = []

  for (const d of currDormies) {
    if (!prevSet.has(d.segment)) {
      const teamName = teams[d.losingTeam]?.name || `Team ${d.losingTeam + 1}`
      const segLabel = SEGMENT_LABELS[d.segment]

      events.push({
        game_id: gameId,
        match_id: matchId,
        event_type: `dormie_${d.segment}`,
        player_id: null,
        hole_number: holeNumber,
        headline: HEADLINES.dormie(gameName, teamName, segLabel, d.remaining),
      })
    }
  }

  return events
}

// ============================================
// Match event detection (ties and lead flips)
// ============================================

/**
 * Detect match-level events by comparing standings before and after a score.
 */
export function detectMatchEvents({ prevResults, currResults, gameId, matchId, holeNumber, teams, gameName }) {
  if (!prevResults || !currResults || !teams || teams.length < 2) return []

  const events = []

  const getLeader = (results) => {
    if (!results?.standings) return null
    if (results.format === 'nassau') {
      const overall = results.nassau?.overall
      if (!overall) return null
      // Stroke play nassau (Duels): use aggregate totals
      if (results.type === 'stroke_play' && overall.totals) {
        const min = Math.min(...overall.totals)
        if (overall.totals.every((t) => t === 0)) return null
        const leaders = overall.totals.map((t, i) => ({ idx: i, total: t })).filter((t) => t.total === min)
        return leaders.length === 1 ? leaders[0].idx : null
      }
      // Match play nassau: use hole wins
      const maxWins = Math.max(...overall.wins)
      if (maxWins === 0) return null
      const leaders = overall.wins.map((w, i) => ({ idx: i, wins: w })).filter((t) => t.wins === maxWins)
      return leaders.length === 1 ? leaders[0].idx : null // null = tied
    }
    if (results.format === 'per_nine') {
      const pts = results.standings.map((s) => s.points)
      const max = Math.max(...pts)
      if (max === 0) return null
      const leaders = pts.map((p, i) => ({ idx: i, pts: p })).filter((t) => t.pts === max)
      return leaders.length === 1 ? leaders[0].idx : null
    }
    return null
  }

  const prevLeader = getLeader(prevResults)
  const currLeader = getLeader(currResults)

  // Detect tie: there was a leader, now it's tied
  if (prevLeader != null && currLeader == null) {
    events.push({
      game_id: gameId,
      match_id: matchId,
      event_type: 'match_tied',
      player_id: null,
      hole_number: holeNumber,
      headline: HEADLINES.match_tied(gameName, holeNumber),
    })
  }

  // Detect lead flip: leader changed to a different team
  if (currLeader != null && prevLeader != null && currLeader !== prevLeader) {
    const teamName = teams[currLeader]?.name || `Team ${currLeader + 1}`
    events.push({
      game_id: gameId,
      match_id: matchId,
      event_type: 'lead_flip',
      player_id: null,
      hole_number: holeNumber,
      headline: HEADLINES.lead_flip(gameName, teamName, holeNumber),
    })
  }

  return events
}

// ============================================
// Score summary generation
// ============================================

/**
 * Build a score summary event for the ticker.
 * Shows full segment breakdown: "Game 1: Team A wins front 2&1 | 1UP back | 2UP total"
 */
export function buildScoreSummary({ roundResults, gameId, matchId, holeNumber, teams, gameName }) {
  if (!roundResults || !teams || teams.length < 2) return null

  const parts = []

  if (roundResults.format === 'nassau' && roundResults.nassau) {
    for (const seg of ['front', 'back', 'overall']) {
      const data = roundResults.nassau[seg]
      if (!data) continue

      // Skip segments with no holes played
      const hasActivity = data.wins?.some((w) => w > 0) ||
        (data.totals && data.totals.some((t) => t !== 0))
      if (!hasActivity) continue

      const segLabel = seg === 'overall' ? 'total' : seg

      if (roundResults.type === 'stroke_play' && data.totals) {
        // Stroke play: compare totals (lower is better)
        const diff = data.totals[0] - data.totals[1]
        if (data.complete && data.winner != null) {
          const winnerName = teams[data.winner]?.name || `Team ${data.winner + 1}`
          parts.push(`${winnerName} wins ${segLabel}`)
        } else if (diff === 0) {
          parts.push(`tied ${segLabel}`)
        } else {
          const leader = diff < 0 ? 0 : 1
          const leaderName = teams[leader]?.name || `Team ${leader + 1}`
          parts.push(`${leaderName} leads by ${Math.abs(diff)} ${segLabel}`)
        }
      } else {
        // Match play: compare wins — only say "wins" if segment is complete or clinched
        const ranges = { front: [1, 9], back: [10, 18], overall: [1, 18] }
        const [start, end] = ranges[seg]
        const status = computeSegmentMatchStatus(roundResults.holeResults, start, end)
        const isFinished = data.complete || status?.clinchHole

        if (isFinished && data.winner != null) {
          const clinchText = status?.clinchHole ? status.text : ''
          const winnerName = teams[data.winner]?.name || `Team ${data.winner + 1}`
          parts.push(clinchText
            ? `${winnerName} wins ${segLabel} ${clinchText}`
            : `${winnerName} wins ${segLabel}`)
        } else {
          const diff = data.wins[0] - data.wins[1]
          if (diff === 0) {
            parts.push(`AS ${segLabel}`)
          } else {
            const leader = diff > 0 ? 0 : 1
            const leaderName = teams[leader]?.name || `Team ${leader + 1}`
            parts.push(`${leaderName} ${Math.abs(diff)}UP ${segLabel}`)
          }
        }
      }
    }
  } else if (roundResults.format === 'per_nine' && roundResults.nineResults) {
    // Per-nine format (Six Pack)
    roundResults.nineResults.forEach((nr, idx) => {
      const segLabel = idx === 0 ? 'front' : 'back'
      if (!nr.teamWins || nr.teamWins.every((w) => w === 0)) return

      const nrComplete = (nr.teamWins[0] + nr.teamWins[1]) >= 9
      if (nrComplete && nr.winner != null) {
        const winnerName = teams[nr.winner]?.name || `Team ${nr.winner + 1}`
        parts.push(`${winnerName} wins ${segLabel}`)
      } else {
        const diff = nr.teamWins[0] - nr.teamWins[1]
        if (diff === 0) {
          parts.push(`tied ${segLabel}`)
        } else {
          const leader = diff > 0 ? 0 : 1
          const leaderName = teams[leader]?.name || `Team ${leader + 1}`
          parts.push(`${leaderName} ${Math.abs(diff)}UP ${segLabel}`)
        }
      }
    })
  }

  if (parts.length === 0) return null

  const headline = gameName ? `${gameName}: ${parts.join(' | ')}` : parts.join(' | ')

  return {
    game_id: gameId,
    match_id: matchId,
    event_type: 'score_summary',
    player_id: null,
    hole_number: holeNumber,
    headline,
  }
}
